/**
 * ============================================================================
 * Court Formatters - Utility functions for displaying court data
 * ============================================================================
 *
 * Clean business logic extracted from JSX
 *
 * ============================================================================
 */

import { CURRENCY_SYMBOL } from './currency';
import type { Court } from '../types/courts';

/**
 * Format currency in Nigerian Naira
 */
export function formatCurrency(amount: number | string): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return `${CURRENCY_SYMBOL}0`;
  return `${CURRENCY_SYMBOL}${num.toLocaleString()}`;
}

/**
 * Get court pricing display information
 * Shows the appropriate rate based on the court's effective pricing mode
 */
export function getCourtPricingDisplay(court: Court): {
  label: string;
  sublabel: string;
  className: string;
} {
  const mode = court.effective_pricing_mode || court.pricing_mode || 'hourly';
  const hourlyRate = court.effective_hourly_rate || 0;
  const perPersonRate = (court as any).effective_per_person_rate || 0;

  // Determine source label
  let sublabel = 'Using venue base price';
  let className = 'text-gray-500';

  if (court.pricing_source === 'court_custom') {
    sublabel = 'Court custom price';
    className = 'text-green-600';
  } else if (court.pricing_source === 'sport_custom') {
    sublabel = `Using ${court.sport?.name || 'sport'} price`;
    className = 'text-blue-600';
  }

  // Also check per-person custom rate for source detection
  if (court.custom_per_person_rate != null && mode !== 'hourly') {
    sublabel = 'Court custom price';
    className = 'text-green-600';
  }

  // Format label based on pricing mode
  if (mode === 'per_person') {
    return {
      label: `${formatCurrency(perPersonRate)}/person`,
      sublabel,
      className,
    };
  } else if (mode === 'both') {
    return {
      label: `${formatCurrency(hourlyRate)}/hr · ${formatCurrency(perPersonRate)}/person`,
      sublabel,
      className,
    };
  }

  // Default: hourly
  return {
    label: `${formatCurrency(hourlyRate)}/hr`,
    sublabel,
    className,
  };
}

/**
 * Format court name for display
 */
export function formatCourtName(
  sportName: string,
  courtType: 'Court' | 'Field',
  courtNumber: number
): string {
  return `${sportName} ${courtType} ${courtNumber}`;
}

/**
 * Get next available court number for a sport
 * Handles gaps in numbering properly
 */
export function getNextCourtNumber(courts: Court[], sportId: number): number {
  const sportCourts = courts.filter((c) => c.sport_id === sportId);

  if (sportCourts.length === 0) return 1;

  // Find highest court number
  const maxNumber = Math.max(...sportCourts.map((c) => c.court_number || 0));

  return maxNumber + 1;
}

/**
 * Format recurring discount summary for display
 */
export function formatRecurringDiscounts(court: Court): string | null {
  const discounts = [
    court.recurring_discount_1_month > 0 && `1m: ${court.recurring_discount_1_month}%`,
    court.recurring_discount_3_months > 0 && `3m: ${court.recurring_discount_3_months}%`,
    court.recurring_discount_6_months > 0 && `6m: ${court.recurring_discount_6_months}%`,
    court.recurring_discount_12_months > 0 && `12m: ${court.recurring_discount_12_months}%`,
  ].filter(Boolean);

  return discounts.length > 0 ? `Discounts: ${discounts.join(', ')}` : null;
}

/**
 * Calculate discount example savings
 */
export function calculateDiscountSavings(
  baseRate: number,
  discountPercent: number,
  weeks: number
): number {
  const totalCost = baseRate * weeks;
  return (totalCost * discountPercent) / 100;
}
